import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { 
  UserGroupIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  KeyIcon,
  UserIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useApi, useFormSubmit } from '../hooks/useApi';
import { usersAPI, clientsAPI, trucksAPI, weighbridgesAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import Table from '../components/Table';
import Button from '../components/Button';
import Input, { Select } from '../components/Input';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelect from '../components/MultiSelect';
import Modal, { ConfirmModal } from '../components/Modal';

const UsersPage = () => {
  const { t } = useTranslation();
  const { isAdmin, isOperator } = useAuth();
  const location = useLocation();
  const isDriversPage = location.pathname === '/drivers';
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState(''); // For admin to filter by user type
  const [clientFilter, setClientFilter] = useState(''); // For admin to filter by assigned client
  const [truckAssignmentFilter, setTruckAssignmentFilter] = useState(''); // For drivers page
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [clients, setClients] = useState([]);

  // Reset to page 1 when any filter changes so filtered results show from the start
  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter, statusFilter, userTypeFilter, clientFilter, truckAssignmentFilter]);

  // API hooks
  const { 
    data: usersData, 
    loading: usersLoading, 
    refetch: refetchUsers 
  } = useApi(() => {
    // Map userTypeFilter to API role param so filtering is server-side (fixes pagination)
    let apiRole = isDriversPage ? 'DRIVER' : roleFilter;
    if (!isDriversPage && userTypeFilter && isAdmin) {
      const roleMap = {
        operators: 'CLIENT',
        drivers: 'DRIVER',
        accountants: 'ACCOUNTANT',
        super: 'SUPER',
        admins: 'ADMIN',
        managers: 'MANAGER',
        peseurs: 'PESEUR'
      };
      if (roleMap[userTypeFilter]) {
        apiRole = roleMap[userTypeFilter];
      } else if (userTypeFilter === 'system_admins') {
        apiRole = 'ADMIN,MANAGER'; // Backend will use roleIn
      } else if (userTypeFilter === 'client_users') {
        apiRole = 'CLIENT,DRIVER'; // Backend will use roleIn
      }
    }
    return usersAPI.getAll({
      page: currentPage,
      limit: 20,
      search,
      role: apiRole,
      status: statusFilter
    });
  }, [currentPage, search, roleFilter, statusFilter, userTypeFilter, isDriversPage, truckAssignmentFilter, clientFilter]);

  const allUsers = usersData?.users || [];
  const pagination = usersData?.pagination || {};
  
  // Apply client-side filters
  const users = allUsers.filter(user => {
    // User type filter (for admin on users page)
    if (!isDriversPage && userTypeFilter && isAdmin) {
      if (userTypeFilter === 'system_admins' && !['ADMIN', 'MANAGER'].includes(user.role)) {
        return false;
      }
      if (userTypeFilter === 'operators' && user.role !== 'CLIENT') {
        return false;
      }
      if (userTypeFilter === 'drivers' && user.role !== 'DRIVER') {
        return false;
      }
      if (userTypeFilter === 'client_users' && !['CLIENT', 'DRIVER'].includes(user.role)) {
        return false;
      }
      if (userTypeFilter === 'accountants' && user.role !== 'ACCOUNTANT') {
        return false;
      }
      if (userTypeFilter === 'super' && user.role !== 'SUPER') {
        return false;
      }
      if (userTypeFilter === 'admins' && user.role !== 'ADMIN') {
        return false;
      }
      if (userTypeFilter === 'managers' && user.role !== 'MANAGER') {
        return false;
      }
      if (userTypeFilter === 'peseurs' && user.role !== 'PESEUR') {
        return false;
      }
    }
    
    // Truck assignment filter (for drivers page)
    if (isDriversPage && truckAssignmentFilter) {
      const hasTrucks = user.assignedTrucks && user.assignedTrucks.length > 0;
      if (truckAssignmentFilter === 'assigned' && !hasTrucks) {
        return false;
      }
      if (truckAssignmentFilter === 'unassigned' && hasTrucks) {
        return false;
      }
    }
    
    // Client filter (for admin on users page)
    if (!isDriversPage && clientFilter && isAdmin) {
      if (user.role === 'CLIENT') {
        if (!user.assignedClient || user.assignedClient.id !== clientFilter) {
          return false;
        }
      } else if (user.role === 'CLIENT') {
        if (!user.clientProfile || user.clientProfile.id !== clientFilter) {
          return false;
        }
      } else if (user.role === 'DRIVER') {
        // Drivers have no client assignment; filter by trucks that belong to this client
        const truckBelongsToClient = (truck) => {
          if (!truck) return false;
          const truckClientIds = (truck.truckTransporteurs || []).flatMap(tt =>
            (tt.transporteur?.transporteurClients || []).map(tc => tc.client?.id).filter(Boolean)
          );
          return truckClientIds.includes(clientFilter);
        };
        const hasTruckForClient = user.assignedTrucks?.some(dt => truckBelongsToClient(dt.truck));
        if (!hasTruckForClient) return false;
      } else {
        // For other roles, if client filter is set, hide them
        return false;
      }
    }
    
    return true;
  });

  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    usersAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refetchUsers();
      },
      onError: (error) => {
        console.error('Create user error:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        console.error('Error response headers:', error.response?.headers);
        console.error('Full error object:', error);
        
        // Show specific validation errors if available
        if (error.response?.data?.errors) {
          const errorMessages = error.response.data.errors.map(err => `${err.param}: ${err.msg}`).join(', ');
          console.error('Validation errors:', error.response.data.errors);
          alert(`Validation errors: ${errorMessages}`);
        } else if (error.response?.data?.message) {
          alert(`Error: ${error.response.data.message}`);
        } else {
          alert(`Error: ${error.message || 'Unknown error'}`);
        }
      },
      successMessage: 'User created successfully'
    }
  );

  // Update user function
  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => {
      if (!selectedUser) throw new Error('No user selected');
      return usersAPI.update(selectedUser.id, data);
    },
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedUser(null);
        refetchUsers();
      },
      onError: (error) => {
        console.error('=== UPDATE USER ERROR ===');
        console.error('Error object:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        console.error('Full error response:', JSON.stringify(error.response?.data, null, 2));
        console.error('========================');
        
        // Show specific validation errors if available
        if (error.response?.data?.errors) {
          const errorMessages = error.response.data.errors.map(err => `${err.param || 'field'}: ${err.msg}`).join(', ');
          console.error('Validation errors:', error.response.data.errors);
          alert(`Validation errors: ${errorMessages}`);
        } else if (error.response?.data?.message) {
          alert(`Error: ${error.response.data.message}`);
        } else {
          alert(`Error: ${error.message || 'Unknown error'}`);
        }
      },
      successMessage: 'User updated successfully'
    }
  );

  // Delete user function
  const { submit: submitDelete, loading: deleteLoading } = useFormSubmit(
    () => {
      if (!selectedUser) throw new Error('No user selected');
      return usersAPI.delete(selectedUser.id);
    },
    {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
        refetchUsers();
      },
      successMessage: 'User deleted successfully'
    }
  );

  // Reset password function
  const { submit: submitResetPassword, loading: resetPasswordLoading } = useFormSubmit(
    (data) => {
      if (!selectedUser) throw new Error('No user selected');
      return usersAPI.resetPassword(selectedUser.id, data);
    },
    {
      onSuccess: () => {
        setIsResetPasswordModalOpen(false);
        setSelectedUser(null);
      },
      successMessage: 'Password reset successfully'
    }
  );

  // Load clients data for admin filter
  React.useEffect(() => {
    if (isAdmin() && !isDriversPage) {
      clientsAPI.getAll()
        .then(response => {
          setClients(response.data.clients || []);
        })
        .catch(error => {
          console.error('Failed to load clients:', error);
          console.error('Error response:', error.response?.data);
        });
    }
  }, [isAdmin, isDriversPage]);

  // Event handlers
  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setIsResetPasswordModalOpen(true);
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-100 text-red-800';
      case 'MANAGER': return 'bg-blue-100 text-blue-800';
      case 'CLIENT': return 'bg-green-100 text-green-800';
      case 'DRIVER': return 'bg-purple-100 text-purple-800';
      case 'ACCOUNTANT': return 'bg-yellow-100 text-yellow-800';
      case 'PESEUR': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  const columns = [
    {
      key: 'user',
      title: 'User',
      render: (value, row) => (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
            <UserIcon className="h-6 w-6 text-gray-600" />
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900">
              {row.firstName} {row.lastName}
            </div>
            <div className="text-sm text-gray-500">@{row.username}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      title: t('users.table.role'),
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(row.role)}`}>
          {t(`roles.${row.role}`)}
        </span>
      )
    },
    
    // Show assigned client column for all users when admin is viewing users page
    ...(!isDriversPage && isAdmin ? [{
      key: 'assignedClient',
      title: t('users.table.assignedClient'),
      render: (value, row) => {
        // For CLIENT role: staff have assignedClient, client logins have clientProfile
        if (row.role === 'CLIENT') {
          if (row.assignedClient) {
            return (
              <div className="text-sm">
                <div className="font-medium text-blue-700">{row.assignedClient.companyName}</div>
                <div className="text-xs text-gray-500">{t('users.table.clientAssignment')}</div>
              </div>
            );
          }
          if (row.clientProfile) {
            return (
              <div className="text-sm">
                <div className="font-medium text-teal-700">{row.clientProfile.companyName}</div>
                <div className="text-xs text-gray-500">{t('users.table.clientLogin')}</div>
              </div>
            );
          }
          return (
            <div className="text-sm">
              <div className="font-medium text-orange-600">{t('users.table.noClient')}</div>
              <div className="text-xs text-orange-500">{t('users.table.needsAssignment')}</div>
            </div>
          );
        }
        
        // For drivers, show only clients from assigned trucks (no direct client assignment)
        if (row.role === 'DRIVER') {
          const allFromTrucks = [...new Set(
            (row.assignedTrucks || []).flatMap(dt =>
              (dt.truck?.truckTransporteurs || []).flatMap(tt =>
                (tt.transporteur?.transporteurClients || []).map(tc => tc.client?.companyName).filter(Boolean)
              )
            )
          )];
          if (allFromTrucks.length > 0) {
            return (
              <div className="text-sm">
                <div className="font-medium text-blue-700">{allFromTrucks.join(', ')}</div>
                <div className="text-xs text-gray-500">{t('users.table.throughTruck')}</div>
              </div>
            );
          }
        }
        
        // For any user with assignedClient or clientProfile (fallback)
        if (row.assignedClient) {
          return (
            <div className="text-sm">
              <div className="font-medium text-blue-700">{row.assignedClient.companyName}</div>
            </div>
          );
        }
        if (row.clientProfile) {
          return (
            <div className="text-sm">
              <div className="font-medium text-teal-700">{row.clientProfile.companyName}</div>
            </div>
          );
        }
        
        // No client assigned
        return (
          <div className="text-sm text-gray-500">
            -
          </div>
        );
      }
    }] : []),
    
    // Show truck assignment column for drivers page
    ...(isDriversPage ? [{
      key: 'truckAssignment',
      title: t('users.table.truckAssignment'),
      render: (value, row) => {
        if (row.assignedTrucks && row.assignedTrucks.length > 0) {
          return (
            <div className="text-sm">
              {row.assignedTrucks.map((dt, index) => (
                <div key={dt.truck?.id || index} className={index > 0 ? 'mt-1' : ''}>
                  <div className="font-medium text-green-700">{dt.truck?.plateNumber || 'N/A'}</div>
                </div>
              ))}
              <div className="text-xs text-gray-500 mt-1">{t('users.assigned')}</div>
            </div>
          );
        } else {
          return (
            <div className="text-sm">
              <div className="font-medium text-orange-600">{t('users.noTruck')}</div>
              <div className="text-xs text-orange-500">{t('users.table.needsAssignment')}</div>
            </div>
          );
        }
      }
    }] : []),
    
    // Show phone number column
    {
      key: 'phone',
      title: t('users.form.phone'),
      render: (value, row) => {
        if (row.phone) {
          return (
            <div className="text-sm text-gray-900">
              {row.phone}
            </div>
          );
        } else {
          return (
            <div className="text-sm text-gray-400">
              -
            </div>
          );
        }
      }
    },

    {
      key: 'status',
      title: 'Status',
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(row.status)}`}>
          {row.status}
        </span>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'center',
      sticky: true,
      width: '220px',
      minWidth: '220px',
      render: (value, row) => (
        <div className="flex flex-wrap items-center justify-center gap-2">
                      <Button
              variant="ghost"
              size="sm"
              onClick={() => handleView(row)}
              className="text-blue-600 hover:text-blue-800"
              title={t('users.actions.view')}
            >
              <EyeIcon className="w-4 h-4" />
            </Button>
            {(isAdmin || isOperator) && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(row)}
                  className="text-indigo-600 hover:text-indigo-800"
                  title={
                    isDriversPage && (!row.assignedTrucks || row.assignedTrucks.length === 0) 
                      ? t('users.editDriverAssignTruck') 
                      : (isDriversPage ? t('users.editDriver') : t('users.editUser'))
                  }
                >
                  <PencilIcon className="w-4 h-4" />
                </Button>
                {isAdmin && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResetPassword(row)}
                      className="text-yellow-600 hover:text-yellow-800"
                      title={t('users.actions.resetPassword')}
                    >
                      <KeyIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(row)}
                      className={row._count?.loads > 0 ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-800"}
                      title={
                        row._count?.loads > 0 
                          ? t('users.tooltips.cannotDeleteWithLoads')
                          : (isDriversPage ? t('users.deleteDriver') : t('users.deleteUser'))
                      }
                      disabled={row._count?.loads > 0}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </>
            )}
        </div>
      )
    }
  ];



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <UserGroupIcon className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isDriversPage ? t('navigation.drivers') : t('navigation.users')}
          </h1>
          <p className="text-gray-600">
{isDriversPage ? t('users.driversSubtitle') : t('users.subtitle')}
          </p>
          {isDriversPage && (
            <div className="flex items-center space-x-4 mt-2">
              <div className="text-sm text-gray-500">
{t('users.totalDrivers')}: <span className="font-medium text-gray-700">{allUsers.length}</span>
              </div>
              <div className="text-sm text-gray-500">
{t('users.needTruck')}: <span className="font-medium text-orange-600">
                  {allUsers.filter(user => !user.assignedTrucks || user.assignedTrucks.length === 0).length}
                </span>
              </div>
              <div className="text-sm text-gray-500">
{t('users.assigned')}: <span className="font-medium text-green-600">
                  {allUsers.filter(user => user.assignedTrucks && user.assignedTrucks.length > 0).length}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      
        {(isAdmin || isOperator) && (
          <Button
            variant="primary"
            size="sm"
            icon={PlusIcon}
            onClick={() => setIsAddModalOpen(true)}
          >
{isDriversPage ? t('users.addDriver') : t('users.addUser')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('users.searchUsers')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {!isOperator && !isDriversPage && (
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                placeholder={t('users.allRoles')}
                options={[
                  { value: 'ADMIN', label: t('roles.ADMIN') },
                  { value: 'MANAGER', label: t('roles.MANAGER') },
                  { value: 'CLIENT', label: t('roles.CLIENT') },
                  { value: 'DRIVER', label: t('roles.DRIVER') },
                  { value: 'ACCOUNTANT', label: t('roles.ACCOUNTANT') },
                  { value: 'SUPER', label: t('roles.SUPER') },
                  { value: 'PESEUR', label: t('roles.PESEUR') },
                ]}
              />
            )}
            
            {/* User Type filter - only show for admin on users page */}
            {!isDriversPage && isAdmin && (
              <Select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                placeholder={t('users.filters.allUserTypes')}
                options={[
                  { value: 'admins', label: t('roles.ADMIN') },
                  { value: 'managers', label: t('roles.MANAGER') },
                  { value: 'operators', label: t('users.filters.operators') },
                  { value: 'drivers', label: t('users.filters.drivers') },
                  { value: 'peseurs', label: t('roles.PESEUR') },
                  { value: 'accountants', label: t('roles.ACCOUNTANT') },
                  { value: 'super', label: t('roles.SUPER') },
                ]}
              />
            )}
            
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              placeholder={t('users.allStatus')}
              options={[
                { value: 'active', label: t('users.form.active') },
                { value: 'inactive', label: t('users.form.inactive') },
                { value: 'pending', label: t('users.form.pending') },
              ]}
            />
            
            {/* Client filter - only show for admin on users page */}
            {!isDriversPage && isAdmin && (
              <SearchableSelect
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                placeholder={t('users.filters.allClients')}
                options={clients.map(client => ({
                  value: client.id,
                  label: client.companyName
                }))}
              />
            )}
            
            {/* Truck Assignment Filter - only show on drivers page */}
            {isDriversPage && (
              <Select
                value={truckAssignmentFilter}
                onChange={(e) => setTruckAssignmentFilter(e.target.value)}
                placeholder={t('users.allDrivers')}
                options={[
                  { value: 'assigned', label: t('users.withTruck') },
                  { value: 'unassigned', label: t('users.needTruck') },
                ]}
              />
            )}
          </div>
          
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setRoleFilter('');
                setStatusFilter('');
                setUserTypeFilter('');
                setClientFilter('');
                setTruckAssignmentFilter('');
                setCurrentPage(1);
              }}
            >
              {t('users.clearFilters')}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Users Table */}
      <div className="card">
        <Table
          columns={columns}
          data={users}
          loading={usersLoading}
          pagination={pagination}
          onPageChange={setCurrentPage}
          emptyMessage={t('users.noUsersFound')}
        />
      </div>

      {/* Add User Modal */}
      <UserFormModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        title={isDriversPage ? t('users.addNewDriver') : t('users.addUser')}
        submitText={isDriversPage ? t('users.createDriver') : t('users.createUser')}
        isDriversPage={isDriversPage}
      />

      {/* Edit User Modal */}
      <UserFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        title={isDriversPage ? t('users.editDriver') : t('users.editUser')}
        submitText={isDriversPage ? t('users.updateDriver') : t('users.updateUser')}
        initialData={selectedUser}
        isEditing
        isDriversPage={isDriversPage}
      />

      {/* View User Modal */}
      <ViewUserModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => {
          setIsResetPasswordModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={submitResetPassword}
        loading={resetPasswordLoading}
        user={selectedUser}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={submitDelete}
        loading={deleteLoading}
        title={isDriversPage ? t('users.deleteDriver') : t('users.deleteUser')}
        message={t('users.confirmDelete', { 
          name: selectedUser 
            ? (`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.username || 'this user')
            : 'this user'
        })}
        confirmText={t('common.delete')}
        confirmVariant="danger"
      />
    </div>
  );
};

// User Form Modal Component
const UserFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  loading, 
  title, 
  submitText, 
  initialData = {}, 
  isEditing = false,
  isDriversPage = false
}) => {
  const { t } = useTranslation();
  const { isOperator, isAdmin, getOperatorClient } = useAuth();
  const [clients, setClients] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [weighbridges, setWeighbridges] = useState([]);
  const [ports, setPorts] = useState([]);
  const [moles, setMoles] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingTrucks, setLoadingTrucks] = useState(false);
  const [loadingWeighbridges, setLoadingWeighbridges] = useState(false);
  const [loadingAccessOptions, setLoadingAccessOptions] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: isOperator ? 'DRIVER' : 'CLIENT',
    status: 'active',
    clientId: '',
    weighbridgeIds: [],
    truckIds: [],
    clientIds: [],
    createdBy: '',
    hasLimitedAccess: isOperator ? false : true, // Managers default to limited access, clients/drivers don't use this
    accessiblePortIds: [],
    accessibleMoleIds: [],
    accountantPortIds: [],
    accountantMoleIds: []
  });

  // Load clients when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setLoadingClients(true);
      clientsAPI.getAll({ limit: 1000 })
        .then(response => {
          setClients(response.data.clients || []);
        })
        .catch(error => {
          console.error('Failed to load clients:', error);
        })
        .finally(() => {
          setLoadingClients(false);
        });
    }
  }, [isOpen]);

  // Load weighbridges when modal opens (for Peseur role)
  React.useEffect(() => {
    if (isOpen && isAdmin()) {
      setLoadingWeighbridges(true);
      weighbridgesAPI.getAll({ limit: 500 })
        .then(response => {
          setWeighbridges(response.data.weighbridges || []);
        })
        .catch(error => {
          console.error('Failed to load weighbridges:', error);
        })
        .finally(() => {
          setLoadingWeighbridges(false);
        });
    }
  }, [isOpen, isAdmin]);

  // Load trucks when modal opens or when client selection changes
  const loadTrucks = React.useCallback(() => {
    if (!isOpen) return;
    
    setLoadingTrucks(true);
    const params = { limit: 1000 };
    
    // For clients, only load trucks from their assigned client
    if (isOperator() && getOperatorClient()) {
      params.clientId = getOperatorClient().id;
    }
    // For admins editing drivers: load all trucks (drivers have only truck assignment, no client)
    
    trucksAPI.getAll(params)
      .then(response => {
        const filteredTrucks = response.data.trucks || [];
        setTrucks(filteredTrucks);
        
        setFormData(prev => {
          if (prev.truckIds && prev.truckIds.length > 0) {
            const validTruckIds = prev.truckIds.filter(truckId => 
              filteredTrucks.find(t => t.id === truckId)
            );
            if (validTruckIds.length !== prev.truckIds.length) {
              return { ...prev, truckIds: validTruckIds };
            }
          }
          return prev;
        });
      })
      .catch(error => {
        console.error('Failed to load trucks:', error);
      })
      .finally(() => {
        setLoadingTrucks(false);
      });
  }, [isOpen, isOperator, getOperatorClient]);

  React.useEffect(() => {
    loadTrucks();
  }, [loadTrucks]);

  // Load access control options when modal opens (Admin only)
  React.useEffect(() => {
    if (isOpen && isAdmin()) {
      setLoadingAccessOptions(true);
      usersAPI.getAccessControlOptions()
        .then(response => {
          setPorts(response.data.ports || []);
          setMoles(response.data.moles || []);
        })
        .catch(error => {
          console.error('Failed to load access control options:', error);
        })
        .finally(() => {
          setLoadingAccessOptions(false);
        });
      
      // Note: Client users list no longer needed since we removed the Created By field for drivers
    }
  }, [isOpen, isAdmin]);

  React.useEffect(() => {
    if (isOpen) {
      // Extract accessible port and mole IDs from the initialData
      const accessiblePortIds = initialData?.accessiblePorts?.map(ap => ap.port.id) || [];
      const accessibleMoleIds = initialData?.accessibleMoles?.map(am => am.mole.id) || [];
      const accountantPortIds = initialData?.accountantPorts?.map(ap => ap.port.id) || [];
      const accountantMoleIds = initialData?.accountantMoles?.map(am => am.mole.id) || [];
      
      setFormData({
        firstName: initialData?.firstName || '',
        lastName: initialData?.lastName || '',
        username: initialData?.username || '',
        email: initialData?.email || '',
        phone: initialData?.phone ?? '',
        password: '',
        confirmPassword: '',
        role: initialData?.role || (isOperator ? 'DRIVER' : 'CLIENT'),
        status: initialData?.status || 'active',
        clientId: initialData?.clientId || '',
        weighbridgeIds: initialData?.peseurWeighbridges?.map(pw => pw.weighbridge?.id).filter(Boolean) || [],
        truckIds: initialData?.assignedTrucks?.map(dt => dt.truck?.id).filter(Boolean) || [],
        clientIds: initialData?.role === 'DRIVER' ? [] : (initialData?.assignedClients?.map(dc => dc.client?.id).filter(Boolean) || []),
        createdBy: initialData?.createdBy || '',
        hasLimitedAccess: initialData?.hasLimitedAccess || false,
        accessiblePortIds,
        accessibleMoleIds,
        accountantPortIds,
        accountantMoleIds
      });
    } else {
      // Reset form when modal closes
      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: isOperator ? 'DRIVER' : 'CLIENT',
        status: 'active',
        clientId: '',
        weighbridgeIds: [],
        truckIds: [],
        clientIds: [],
        createdBy: '',
        hasLimitedAccess: isOperator ? false : true, // Managers default to limited access, clients/drivers don't use this
        accessiblePortIds: [],
        accessibleMoleIds: [],
        accountantPortIds: [],
        accountantMoleIds: []
      });
    }
  }, [isOpen, initialData?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize form values when opening or switching the edited user

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic form validation
    if (!formData.firstName || !formData.lastName || !formData.username || !formData.email) {
      alert(t('users.form.fillRequiredFields'));
      return;
    }
    
    if (!isEditing && !formData.password) {
      alert(t('users.form.passwordRequired'));
      return;
    }
    
    if (!isEditing && !formData.confirmPassword) {
      alert(t('users.form.confirmPasswordRequired'));
      return;
    }
    
    if (!isEditing && formData.password !== formData.confirmPassword) {
      alert(t('users.passwordsDoNotMatch'));
      return;
    }
    
    if (formData.password && formData.password.length < 6) {
      alert(t('users.form.passwordMinLength'));
      return;
    }

    // Validate client assignment for client users
    if (formData.role === 'CLIENT' && !formData.clientId) {
      alert(t('users.clientAssignmentRequired'));
      return;
    }

    // Validate weighbridge assignment for peseurs
    if (formData.role === 'PESEUR' && (!formData.weighbridgeIds || formData.weighbridgeIds.length === 0)) {
      alert(t('users.weighbridgeAssignmentRequired'));
      return;
    }
    
    // Truck assignment is optional for drivers during creation
    // They can be assigned to trucks later through the truck management interface
    
    const submitData = { ...formData };
    

    
    // CRITICAL: Always ensure role is set when editing - use initialData.role as source of truth
    // This must happen FIRST before any role-based logic
    const userRole = isEditing && initialData?.role ? initialData.role : (formData.role || submitData.role);
    if (userRole) {
      submitData.role = userRole;
    }
    
    // Trim string fields to remove leading/trailing whitespace
    Object.keys(submitData).forEach(key => {
      if (typeof submitData[key] === 'string') {
        submitData[key] = submitData[key].trim();
      }
    });
    
    // Remove confirmPassword field (not needed by backend)
    delete submitData.confirmPassword;
    
    // Remove password if editing and it's empty
    if (isEditing && !submitData.password) {
      delete submitData.password;
    }

    // Clean up clientId for non-clients or if empty
    if (submitData.role !== 'CLIENT' || !submitData.clientId || String(submitData.clientId).trim() === '') {
      delete submitData.clientId;
    }

    // Clean up weighbridgeIds for non-peseurs; ensure array for peseurs
    if (submitData.role !== 'PESEUR') {
      delete submitData.weighbridgeIds;
    } else {
      submitData.weighbridgeIds = Array.isArray(submitData.weighbridgeIds)
        ? submitData.weighbridgeIds.filter(id => id && String(id).trim() !== '')
        : [];
    }
    
    // Handle truckIds for drivers - always send array (even if empty) to allow clearing assignments
    // Must check role BEFORE client filtering happens. Coerce to strings for backend validation.
    if (submitData.role === 'DRIVER') {
      if (!submitData.truckIds || !Array.isArray(submitData.truckIds)) {
        submitData.truckIds = [];
      } else {
        submitData.truckIds = submitData.truckIds
          .filter(id => id != null && String(id).trim() !== '')
          .map(id => String(id));
      }
    } else {
      // Remove truckIds for non-drivers
      delete submitData.truckIds;
    }
    
    // Handle clientIds for drivers - always send array (even if empty) to allow clearing assignments
    // Must check role BEFORE operator filtering happens
    // Use the userRole variable we set earlier to ensure we have the correct role
    const roleForCheck = submitData.role || userRole;
    
    if (roleForCheck === 'DRIVER') {
      // Drivers have only truck assignment, no client assignment
      submitData.clientIds = [];
    } else {
      // Remove clientIds for non-drivers
      delete submitData.clientIds;
    }
    
    // Only include createdBy for admins when creating new users
    // When editing, don't send createdBy unless it's being explicitly changed to a different value
    if (!isAdmin()) {
      delete submitData.createdBy;
    } else if (isEditing) {
      // When editing, only send createdBy if it's different from the original
      if (!submitData.createdBy || String(submitData.createdBy).trim() === '') {
        delete submitData.createdBy;
      } else if (initialData?.createdBy && submitData.createdBy === initialData.createdBy) {
        delete submitData.createdBy; // Don't send if unchanged
      }
    } else {
      // When creating, only send if it has a value
      if (!submitData.createdBy || String(submitData.createdBy).trim() === '') {
        delete submitData.createdBy;
      }
    }
    
    // Handle phone field - convert empty strings to null (server will handle null)
    if (submitData.phone !== undefined) {
      if (!submitData.phone || String(submitData.phone).trim() === '') {
        submitData.phone = null;
      }
    }
    
    // For clients, only allow specific fields when editing drivers (not when creating)
    if (isOperator() && isEditing) {
      const allowedFields = ['firstName', 'lastName', 'email', 'phone', 'status', 'truckIds'];
      const cleanedData = {};
      
      // Only include allowed fields
      allowedFields.forEach(field => {
        if (submitData[field] !== undefined) {
          cleanedData[field] = submitData[field];
        }
      });
      
      // Completely replace submitData with only allowed fields
      Object.keys(submitData).forEach(key => delete submitData[key]);
      Object.assign(submitData, cleanedData);
      
      // Ensure we don't send access control fields for clients
      delete submitData.hasLimitedAccess;
      delete submitData.accessiblePortIds;
      delete submitData.accessibleMoleIds;
      delete submitData.accountantPortIds;
      delete submitData.accountantMoleIds;
      delete submitData.role; // Clients can't change role
      delete submitData.username; // Clients can't change username
      // Note: clientIds is not in allowedFields, so it's already removed for clients
    } else if (isAdmin() && isEditing) {
      // For admins editing, ensure role and clientIds are preserved
      // CRITICAL: Always set role from initialData for admins (it's required for driver-specific logic)
      if (initialData?.role) {
        submitData.role = initialData.role;
      }
      // Drivers have only truck assignment
      if (submitData.role === 'DRIVER') {
        submitData.clientIds = [];
      }
    }
    
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value // MultiSelect sends arrays, regular inputs send strings - both work with this
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} closeOnOverlayClick={false}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
{t('users.form.firstName')} *
            </label>
            <Input
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
{t('users.form.lastName')} *
            </label>
            <Input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
{t('users.form.username')} *
          </label>
          <Input
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
{t('users.form.email')} *
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div>
            <Input
              label={t('users.form.phone')}
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('users.form.phonePlaceholder')}
            />
          </div>
        </div>

        {/* Password fields - only show when creating new users */}
        {!isEditing && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('users.form.newPassword')} *
              </label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder={t('users.form.passwordPlaceholder')}
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
{t('users.form.confirmPassword')} *
              </label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder={t('users.form.confirmPasswordPlaceholder')}
                minLength={6}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            {isOperator && isDriversPage ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.form.role')} *
                </label>
                <input
                  type="text"
                  value={t('roles.DRIVER')}
                  disabled
                  className="input w-full bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('users.operatorsCanOnlyCreateDrivers')}
                </p>
              </div>
            ) : (
              <Select
                label={t('users.form.role')}
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                options={[
                  { value: 'CLIENT', label: t('roles.CLIENT') },
                  { value: 'DRIVER', label: t('roles.DRIVER') },
                  { value: 'MANAGER', label: t('roles.MANAGER') },
                  { value: 'ADMIN', label: t('roles.ADMIN') },
                  { value: 'ACCOUNTANT', label: t('roles.ACCOUNTANT') },
                  { value: 'SUPER', label: t('roles.SUPER') },
                  { value: 'PESEUR', label: t('roles.PESEUR') },
                ]}
              />
            )}
          </div>
          
          <div>
            <Select
              label={t('users.form.status')}
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              options={[
                { value: 'active', label: t('users.form.active') },
                { value: 'inactive', label: t('users.form.inactive') },
                { value: 'pending', label: t('users.form.pending') },
              ]}
            />
          </div>
        </div>

        {/* Client Assignment for client users */}
        {formData.role === 'CLIENT' && isAdmin && (
          <div>
            <SearchableSelect
              label={`${t('users.assignedClient')} `}
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              required
              disabled={loadingClients}
              placeholder={loadingClients ? t('common.loading') : t('users.selectClient')}
              options={clients.map(client => ({
                value: client.id,
                label: client.companyName
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('users.operatorClientDescription')}
            </p>
          </div>
        )}

        {/* Weighbridge Assignment for Peseurs */}
        {formData.role === 'PESEUR' && isAdmin && (
          <div>
            <MultiSelect
              label={t('users.assignedWeighbridge')}
              name="weighbridgeIds"
              values={formData.weighbridgeIds || []}
              onChange={handleChange}
              required
              disabled={loadingWeighbridges}
              placeholder={loadingWeighbridges ? t('common.loading') : t('users.selectWeighbridge')}
              options={weighbridges.map(wb => ({
                value: wb.id,
                label: `${wb.name}${wb.location ? ` - ${wb.location}` : ''}`
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('users.peseurWeighbridgeDescription')}
            </p>
          </div>
        )}

        {/* Truck Assignment for Drivers - drivers have only truck assignment, no client */}
        {formData.role === 'DRIVER' && (
          <div>
            <MultiSelect
              label={`${t('users.assignedTruck')} (${t('common.optional')})`}
              name="truckIds"
              values={formData.truckIds || []}
              onChange={handleChange}
              disabled={loadingTrucks}
              placeholder={loadingTrucks ? t('common.loading') : t('users.selectTruck')}
              options={trucks.map(truck => ({
                value: truck.id,
                label: `${truck.plateNumber}${Array.isArray(truck.transporteurs) && truck.transporteurs.length ? ` - ${truck.transporteurs.map(t => t.companyName).join(', ')}` : ''}`
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isOperator 
                ? t('users.truckAssignmentOperatorInfo')
                : t('users.truckAssignmentAdminInfo')
            }
            </p>
          </div>
        )}

        {/* Access Control for Managers */}
        {formData.role === 'MANAGER' && !isOperator && (
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900">{t('users.accessControl')}</h4>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="hasLimitedAccess"
                  checked={formData.hasLimitedAccess}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    hasLimitedAccess: e.target.checked,
                    // Clear selections when toggling
                    accessiblePortIds: e.target.checked ? prev.accessiblePortIds : [],
                    accessibleMoleIds: e.target.checked ? prev.accessibleMoleIds : []
                  }))}
                  className="checkbox-app"
                />
                <span className="ml-2 text-sm text-gray-700">
{t('users.limitAccessToPortsMoles')}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                {formData.hasLimitedAccess 
                  ? t('users.limitedAccessDescription') || 'Sélectionnez les ports et moles auxquels ce gestionnaire aura accès.'
                  : t('users.fullAccessInfo')
                }
              </p>
            </div>

            {formData.hasLimitedAccess && (
              <>
                {/* Port Access */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('users.accessiblePorts')}
                  </label>
                  {loadingAccessOptions ? (
                    <div className="text-sm text-gray-500">{t('users.loadingPorts')}</div>
                  ) : (
                    <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                      {ports.map(port => (
                        <label key={port.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.accessiblePortIds.includes(port.id)}
                            onChange={(e) => {
                              const portId = port.id;
                              setFormData(prev => ({
                                ...prev,
                                accessiblePortIds: e.target.checked
                                  ? [...prev.accessiblePortIds, portId]
                                  : prev.accessiblePortIds.filter(id => id !== portId)
                              }));
                            }}
                            className="checkbox-app"
                          />
                          <span className="ml-2 text-sm">
                            {port.name} - {port.location}
                          </span>
                        </label>
                      ))}
                      {ports.length === 0 && (
                        <div className="text-sm text-gray-500">{t('users.noPortsAvailable')}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Mole Access */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
{t('users.accessibleMoles')}
                  </label>
                  {loadingAccessOptions ? (
                    <div className="text-sm text-gray-500">{t('users.loadingMoles')}</div>
                  ) : (
                    <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                      {moles.map(mole => (
                        <label key={mole.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.accessibleMoleIds.includes(mole.id)}
                            onChange={(e) => {
                              const moleId = mole.id;
                              setFormData(prev => ({
                                ...prev,
                                accessibleMoleIds: e.target.checked
                                  ? [...prev.accessibleMoleIds, moleId]
                                  : prev.accessibleMoleIds.filter(id => id !== moleId)
                              }));
                            }}
                            className="checkbox-app"
                          />
                          <span className="ml-2 text-sm">
                            {mole.name} ({mole.port.name})
                          </span>
                        </label>
                      ))}
                      {moles.length === 0 && (
                        <div className="text-sm text-gray-500">{t('users.noMolesAvailable')}</div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Access Control for Accountants: which ports/zones this accountant can see bills for */}
        {formData.role === 'ACCOUNTANT' && !isOperator && (
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900">{t('users.accessControl')}</h4>
            <p className="text-xs text-gray-500">
              {t('users.accountantAccessDescription') || "Sélectionnez les ports et zones (jetées/môles) dont ce comptable pourra voir les factures. Sans sélection, seules les factures de rechargement de crédit seront visibles."}
            </p>

            {/* Port Access */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('users.accessiblePorts')}
              </label>
              {loadingAccessOptions ? (
                <div className="text-sm text-gray-500">{t('users.loadingPorts')}</div>
              ) : (
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                  {ports.map(port => (
                    <label key={port.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.accountantPortIds.includes(port.id)}
                        onChange={(e) => {
                          const portId = port.id;
                          setFormData(prev => ({
                            ...prev,
                            accountantPortIds: e.target.checked
                              ? [...prev.accountantPortIds, portId]
                              : prev.accountantPortIds.filter(id => id !== portId)
                          }));
                        }}
                        className="checkbox-app"
                      />
                      <span className="ml-2 text-sm">
                        {port.name} - {port.location}
                      </span>
                    </label>
                  ))}
                  {ports.length === 0 && (
                    <div className="text-sm text-gray-500">{t('users.noPortsAvailable')}</div>
                  )}
                </div>
              )}
            </div>

            {/* Mole Access */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('users.accessibleMoles')}
              </label>
              {loadingAccessOptions ? (
                <div className="text-sm text-gray-500">{t('users.loadingMoles')}</div>
              ) : (
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                  {moles.map(mole => (
                    <label key={mole.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.accountantMoleIds.includes(mole.id)}
                        onChange={(e) => {
                          const moleId = mole.id;
                          setFormData(prev => ({
                            ...prev,
                            accountantMoleIds: e.target.checked
                              ? [...prev.accountantMoleIds, moleId]
                              : prev.accountantMoleIds.filter(id => id !== moleId)
                          }));
                        }}
                        className="checkbox-app"
                      />
                      <span className="ml-2 text-sm">
                        {mole.name} ({mole.port.name})
                      </span>
                    </label>
                  ))}
                  {moles.length === 0 && (
                    <div className="text-sm text-gray-500">{t('users.noMolesAvailable')}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="outline" onClick={onClose}>
{t('common.cancel')}
          </Button>
          <Button type="submit" loading={loading}>
            {submitText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/** Activity summary counts: managers use managed* relations; peseurs use weighbridge assignments. */
function getUserActivitySummaryCounts(u) {
  const loads = u?._count?.loads ?? 0;
  if (u?.role === 'PESEUR') {
    const assignments = u.peseurWeighbridges || [];
    const portIds = new Set();
    let weighbridgeCount = 0;
    assignments.forEach((pw) => {
      const wb = pw?.weighbridge;
      if (!wb?.id) return;
      weighbridgeCount += 1;
      const portId = wb.portId ?? wb.port?.id;
      if (portId) portIds.add(String(portId));
    });
    return { loads, ports: portIds.size, weighbridges: weighbridgeCount };
  }
  return {
    loads,
    ports: u?._count?.managedPorts ?? 0,
    weighbridges: u?._count?.managedWeighbridges ?? 0
  };
}

// View User Modal Component
const ViewUserModal = ({ isOpen, onClose, user: initialUser }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(false);
  
  // Fetch full user details when modal opens
  React.useEffect(() => {
    if (isOpen && initialUser?.id) {
      setLoading(true);
      usersAPI.getById(initialUser.id)
        .then(response => {
          setUser(response.data.user);
        })
        .catch(error => {
          console.error('Failed to fetch user details:', error);
          // Fallback to initial user data if fetch fails
          setUser(initialUser);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setUser(initialUser);
    }
  }, [isOpen, initialUser]);
  
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-100 text-red-800';
      case 'MANAGER': return 'bg-blue-100 text-blue-800';
      case 'CLIENT': return 'bg-green-100 text-green-800';
      case 'DRIVER': return 'bg-purple-100 text-purple-800';
      case 'ACCOUNTANT': return 'bg-yellow-100 text-yellow-800';
      case 'PESEUR': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  if (!user) return null;

  const activitySummary = user._count ? getUserActivitySummaryCounts(user) : null;
  
  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={t('users.details.userDetails')}>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('users.details.userDetails')}>
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
            <UserIcon className="h-8 w-8 text-gray-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {user.firstName || ''} {user.lastName || ''}
            </h3>
            <p className="text-sm text-gray-500">@{user.username || ''}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.table.email')}</label>
            <p className="mt-1 text-sm text-gray-900">{user.email || '-'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.form.phone')}</label>
            <p className="mt-1 text-sm text-gray-900">{user.phone || '-'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.table.role')}</label>
            <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role || '')}`}>
              {user.role ? t(`roles.${user.role}`) : '-'}
            </span>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.table.status')}</label>
            <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(user.status || '')}`}>
              {user.status ? t(`users.status.${user.status}`) : '-'}
            </span>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.table.created')}</label>
            <p className="mt-1 text-sm text-gray-900">{user.createdAt ? formatDate(user.createdAt) : '-'}</p>
          </div>
        </div>

        {/* Role-specific assignments */}
        {user.role === 'CLIENT' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.table.assignedClient')}</label>
            {user.clientProfile ? (
              <div className="mt-1 p-3 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm font-medium text-teal-900">{user.clientProfile.companyName}</p>
                <p className="text-xs text-teal-700">{t('users.table.clientLogin')}</p>
              </div>
            ) : (
              <div className="mt-1 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">{t('users.table.noClient')}</p>
              </div>
            )}
          </div>
        )}
        {user.role === 'CLIENT' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Assigned Client</label>
            {user.assignedClient ? (
              <div className="mt-1 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-900">{user.assignedClient.companyName}</p>
                <p className="text-xs text-green-700">Client ID: {user.assignedClient.id}</p>
              </div>
            ) : (
              <div className="mt-1 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">No client assigned</p>
              </div>
            )}
          </div>
        )}

        {/* Truck assignment only for drivers (no direct client assignment) */}
        {user.role === 'DRIVER' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('users.assignedTruck')}</label>
            {user.assignedTrucks && user.assignedTrucks.length > 0 ? (
              <div className="mt-1 space-y-2">
                {user.assignedTrucks.map((dt, index) => (
                  <div key={dt.truck?.id || index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-900">{t('trucks.plateNumber')}: {dt.truck?.plateNumber || 'N/A'}</p>
                    {(() => {
                      const truckTransporteurs = [...new Set(
                        (dt.truck?.truckTransporteurs || []).map(tt => tt.transporteur?.companyName).filter(Boolean)
                      )];
                      return truckTransporteurs.length > 0 ? (
                        <div className="mt-1">
                          <p className="text-xs font-medium text-blue-800 mb-1">{t('trucks.clientsViaCarriers')}:</p>
                          <div className="text-xs text-blue-700">{truckTransporteurs.join(', ')}</div>
                        </div>
                      ) : null;
                    })()}
                    {dt.truck?.assignedDrivers && dt.truck.assignedDrivers.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-300">
                        <p className="text-xs font-medium text-blue-800 mb-1">{t('users.assignedDrivers')}:</p>
                        <div className="space-y-1">
                          {dt.truck.assignedDrivers.map((ad, driverIndex) => (
                            <div key={ad.driver?.id || driverIndex} className="text-xs text-blue-700">
                              <span className="font-medium">
                                {ad.driver?.firstName} {ad.driver?.lastName}
                              </span>
                              {ad.driver?.username && (
                                <span className="text-blue-600"> (@{ad.driver.username})</span>
                              )}
                              {ad.driver?.phone && (
                                <span className="text-blue-500"> • {ad.driver.phone}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-1 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">{t('users.noTruckAssigned')}</p>
              </div>
            )}
          </div>
        )}

        {/* Access Control Information for Managers */}
        {user.role === 'PESEUR' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('users.assignedWeighbridge')}
            </label>
            {user.peseurWeighbridges?.length > 0 ? (
              <ul className="mt-1 space-y-2">
                {user.peseurWeighbridges.map((pw) => {
                  const wb = pw?.weighbridge;
                  if (!wb) return null;
                  const portName = wb.port?.name;
                  const label = [wb.name, wb.location, portName ? `(${portName})` : '']
                    .filter(Boolean)
                    .join(' — ');
                  return (
                    <li
                      key={wb.id}
                      className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-950"
                    >
                      {label || wb.id}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mt-1 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">{t('users.noneAssigned')}</p>
              </div>
            )}
          </div>
        )}

        {(user.role === 'MANAGER' || user.role === 'ACCOUNTANT') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.accessControl')}</label>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              {user.role === 'MANAGER' && (
                <div className="flex items-center mb-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.hasLimitedAccess ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {user.hasLimitedAccess ? t('users.limitedAccess') : t('users.fullAccess')}
                  </span>
                </div>
              )}
              
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('users.accessiblePorts')}: </span>
                  <span className="text-sm text-gray-900">
                    {(() => {
                      if (user.role === 'MANAGER') {
                        // Check both accessiblePorts (for limited access) and managedPorts (for multiple managers)
                        const accessiblePorts = user.accessiblePorts?.map(ap => ap.port.name) || [];
                        const managedPorts = user.managedPorts?.map(mp => mp.port.name) || [];
                        const allPorts = [...new Set([...accessiblePorts, ...managedPorts])]; // Remove duplicates
                        return allPorts.length > 0 ? allPorts.join(', ') : t('users.noneAssigned');
                      } else if (user.role === 'ACCOUNTANT') {
                        // For accountants, check accountantPorts
                        const accountantPorts = user.accountantPorts?.map(ap => ap.port.name) || [];
                        return accountantPorts.length > 0 ? accountantPorts.join(', ') : t('users.noneAssigned');
                      }
                      return t('users.noneAssigned');
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('users.accessibleMoles')}: </span>
                  <span className="text-sm text-gray-900">
                    {(() => {
                      if (user.role === 'MANAGER') {
                        // Check both accessibleMoles (for limited access) and managedMoles (for multiple managers)
                        const accessibleMoles = user.accessibleMoles?.map(am => `${am.mole.name} (${am.mole.port.name})`) || [];
                        const managedMoles = user.managedMoles?.map(mm => `${mm.mole.name} (${mm.mole.port.name})`) || [];
                        const allMoles = [...new Set([...accessibleMoles, ...managedMoles])]; // Remove duplicates
                        
                        // Check if manager has any port access (if so, they have access to all moles in those ports)
                        const accessiblePorts = user.accessiblePorts?.map(ap => ap.port.name) || [];
                        const managedPorts = user.managedPorts?.map(mp => mp.port.name) || [];
                        const hasPortAccess = accessiblePorts.length > 0 || managedPorts.length > 0;
                        
                        // Only show "Aucun assigné" if no specific moles are assigned AND no port access
                        if (allMoles.length > 0) {
                          return allMoles.join(', ');
                        } else if (hasPortAccess) {
                          // If manager has port access, they have access to all moles in those ports
                          return t('users.allMolesInPorts') || 'Toutes les moles des ports assignés';
                        } else {
                          return t('users.noneAssigned');
                        }
                      } else if (user.role === 'ACCOUNTANT') {
                        // For accountants, check accountantMoles
                        const accountantMoles = user.accountantMoles?.map(am => `${am.mole.name} (${am.mole.port.name})`) || [];
                        
                        // Check if accountant has any port access (if so, they have access to all moles in those ports)
                        const accountantPorts = user.accountantPorts?.map(ap => ap.port.name) || [];
                        const hasPortAccess = accountantPorts.length > 0;
                        
                        // Only show "Aucun assigné" if no specific moles are assigned AND no port access
                        if (accountantMoles.length > 0) {
                          return accountantMoles.join(', ');
                        } else if (hasPortAccess) {
                          // If accountant has port access, they have access to all moles in those ports
                          return t('users.allMolesInPorts') || 'Toutes les moles des ports assignés';
                        } else {
                          return t('users.noneAssigned');
                        }
                      }
                      return t('users.noneAssigned');
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activitySummary && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.details.activitySummary')}</label>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div key="loads" className="bg-blue-50 p-3 rounded-lg">
                <div className="text-lg font-semibold text-blue-600">{activitySummary.loads}</div>
                <div className="text-sm text-blue-800">{t('users.table.loads')}</div>
              </div>
              <div key="ports" className="bg-green-50 p-3 rounded-lg">
                <div className="text-lg font-semibold text-green-600">{activitySummary.ports}</div>
                <div className="text-sm text-green-800">{t('users.table.ports')}</div>
              </div>
              <div key="weighbridges" className="bg-purple-50 p-3 rounded-lg">
                <div className="text-lg font-semibold text-purple-600">{activitySummary.weighbridges}</div>
                <div className="text-sm text-purple-800">{t('users.table.weighbridges')}</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
{t('users.details.close')}
          </Button>
      </div>
    </div>
    </Modal>
  );
};

// Reset Password Modal Component
const ResetPasswordModal = ({ isOpen, onClose, onSubmit, loading, user }) => {
  const { t } = useTranslation();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert(t('users.passwordsDoNotMatch'));
      return;
    }
    onSubmit({ newPassword });
    setNewPassword('');
    setConfirmPassword('');
  };

  React.useEffect(() => {
    if (!isOpen) {
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [isOpen]);

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('users.resetPassword')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
{t('users.form.resetPasswordFor')} <strong>{user.firstName || ''} {user.lastName || ''}</strong>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('users.form.newPassword')} *
          </label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('users.form.confirmPassword')} *
          </label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={loading} variant="primary">
            {t('users.resetPassword')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UsersPage;